import { Switch } from "../../primitives/Switch";
import { Control, Controller } from "react-hook-form";
import { FieldPath, FieldPathValue, FieldValues } from "react-hook-form";
import { cn } from "@gettactic/helpers";
import { ComponentPropsWithoutRef, useId } from "react";

export declare type ToggleInputItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  control?: Control<TFieldValues>;
  name: TName;
  title: string;
  description: string;
  defaultValue?: FieldPathValue<TFieldValues, TName>;
  switchProps?: Omit<ComponentPropsWithoutRef<typeof Switch>, "checked"> & {
    onCheckedChange?: (checked: boolean) => void;
  };
  descriptionClassName?: string;
};

/**
 * A settings grid row (title, description, switch) bound to a react-hook-form `control`.
 * @deprecated Prefer `HookFormToggleInput` with `togglePosition="right"`, which also shows
 * errors and supports the info tooltip.
 */
export const ToggleInputItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: ToggleInputItemProps<TFieldValues, TName>
) => {
  const { control, defaultValue, name, title, description, switchProps } =
    props;
  const { onCheckedChange: customOnCheckedChange, ...restSwitchProps } =
    switchProps ?? {};
  // The switch is named by the title and described by the description next to it
  const titleId = useId();
  const descriptionId = useId();

  return (
    <div className="col-span-12 grid grid-cols-12 items-center justify-between gap-x-8">
      <div className="col-span-10 flex flex-col">
        <p id={titleId} className="font-medium text-neutral-900">
          {title}
        </p>
        <p
          id={descriptionId}
          className={cn(
            "pr-10 text-justify text-content",
            props.descriptionClassName
          )}
        >
          {description}
        </p>
      </div>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <Switch
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            checked={!!value}
            onCheckedChange={(checked) => {
              onChange(checked);
              customOnCheckedChange?.(checked);
            }}
            className="col-span-2 ml-4 justify-self-end"
            {...restSwitchProps}
          />
        )}
      />
    </div>
  );
};
